import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  FileTextOutlined,
  SaveOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRealtimeRunsWithTag } from "@trigger.dev/react-hooks";
import { type RealtimeRun } from "@trigger.dev/sdk";
import {
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  Divider,
  message,
  Popconfirm,
  Progress,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { type TFunction } from "i18next";
import groupBy from "lodash.groupby";
import { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { type DishWorkflowSchemaType } from "@fitness-app/ai";
import { type AiAgentRequest } from "@fitness-app/data-models/entities/AiAgentRequest";
import { type Dish, type DishIngredient } from "@fitness-app/data-models/entities/Dish";
import { getErrorMessage } from "@fitness-app/utils";
import { calculateMeasureMultiplier } from "@fitness-app/utils/src/nutrition/calculateMeasureMultiplier";
import { recalculateMeasures } from "@fitness-app/utils/src/nutrition/recalculateMeasures";
import { cn } from "@fitness-app/utils/src/styles/cn";

import { useUserRole } from "~/hooks/trainer/useUserRole";
import { useNutritionMeasures } from "~/modules/Nutrition/hooks/useNutritionMeasures";
import { supabase } from "~/store/initializeStore";
import { AiRequestErrorAlert } from "./AiRequestErrorAlert";
import { useBackgroundTasks } from "./BackgroundTasksProvider";
import { useBackgroundTaskFallback, useBackgroundTasksQuery, type BackgroundTask } from "./useBackgroundTasks";
import { useDishTaskSave, type DishTaskSave } from "./useDishTaskSave";

const AUDIT_DAY_LABELS: Record<string, string> = {
  monday: "Poniedziałek",
  tuesday: "Wtorek",
  wednesday: "Środa",
  thursday: "Czwartek",
  friday: "Piątek",
  saturday: "Sobota",
  sunday: "Niedziela",
};

const AUDIT_MEAL_LABELS: Record<string, string> = {
  BREAKFAST: "Śniadanie",
  SECOND_BREAKFAST: "II śniadanie",
  LUNCH: "Lunch",
  DINNER: "Obiad",
  SNACK: "Przekąska",
  SUPPER: "Kolacja",
};

const AUDIT_REASON_LABELS: Record<string, string> = {
  wykluczenie: "wykluczenia",
  powtorka: "powtórka",
  "kcal-posilku": "kaloryka posiłku",
  "makro-skladniki": "korekta składników",
  makro: "dopasowanie makro",
};

const AUDIT_METRIC_LABELS: Record<string, string> = {
  kcal: "kalorie",
  protein: "białko",
  carbs: "węglowodany",
  fat: "tłuszcz",
};

type AuditAdjustment = {
  day: string;
  mealType: string;
  kind: "swap" | "scale" | "recipe";
  dishName: string;
  fromName?: string;
  fromKcal: number;
  toKcal: number;
  factor?: number;
  reason?: string;
};

type AuditWarning = {
  day?: string;
  metric?: string;
  value?: number;
  target?: number;
  level: "warning" | "critical";
  note?: string;
};

type RunStatus =
  | "PENDING_VERSION"
  | "QUEUED"
  | "DEQUEUED"
  | "EXECUTING"
  | "WAITING"
  | "COMPLETED"
  | "CANCELED"
  | "FAILED"
  | "CRASHED"
  | "SYSTEM_FAILURE"
  | "DELAYED"
  | "EXPIRED"
  | "TIMED_OUT"
  | "STARTED"
  | "SKIPPED";

type TaskRun = RealtimeRun<any>;

type RequestRecording = {
  taskId: string;
  recordingAnalysisId?: string;
  fileName: string;
  recordedAt?: string | null;
  path?: string;
  status?: string;
};

type SalesCallTaskResult = {
  status?: string;
  fileName?: string;
  recordedAt?: string | null;
  durationSeconds?: number | null;
  meetingId?: string;
  transcript?: string;
  analysis?: {
    score?: number;
    summary?: string;
  };
  error?: string;
};

const normalizeIngredientForDisplay = (item: unknown): DishIngredient | null => {
  if (!item || typeof item !== "object") return null;
  const obj = item as Record<string, unknown>;
  const calories =
    (obj.nutrients as { calories?: number } | undefined)?.calories ??
    (typeof obj.calories === "number" ? obj.calories : 0);
  const portion = typeof obj.portion === "number" ? obj.portion : 0;
  const name = typeof obj.name === "string" ? obj.name : "—";
  const id = typeof obj.id === "string" ? obj.id : String(Math.random());
  const measure = typeof obj.measure === "string" ? obj.measure : "g";
  const measureWeight = typeof obj.measureWeight === "number" ? obj.measureWeight : 100;
  const measures: Array<{ id: number; name: string; unit: string; energyPerUnit: number; weightPerUnit: number }> =
    Array.isArray(obj.measures) && obj.measures.length > 0
      ? (obj.measures as Array<{
          id: number;
          name: string;
          unit: string;
          energyPerUnit: number;
          weightPerUnit: number;
        }>)
      : [{ id: 1, name: "g", unit: "grams", energyPerUnit: calories, weightPerUnit: 100 }];
  const nutrients = { ...((obj.nutrients as Record<string, unknown>) || {}), calories };
  return {
    ...obj,
    id,
    name,
    portion,
    measure,
    measureWeight,
    measures,
    nutrients,
    portionCalories: (calories * portion) / 100,
  } as DishIngredient;
};

const UpdatedIngredientsList = ({ ingredients }: { ingredients: DishIngredient[] }) => {
  const { measuresMap } = useNutritionMeasures();

  if (!Array.isArray(ingredients) || ingredients.length === 0) {
    return <Typography.Text type="secondary">Brak składników</Typography.Text>;
  }

  return (
    <div className="space-y-2">
      {ingredients.map((item) => {
        const ingredient = normalizeIngredientForDisplay(item);
        if (!ingredient) return null;

        const enhancedIngredient = recalculateMeasures(ingredient);
        const { multiplier, measureName } = calculateMeasureMultiplier(enhancedIngredient, measuresMap);
        const hasMeasure = measureName && measureName !== "none";

        return (
          <div key={ingredient.id} className="flex flex-col gap-1 rounded border border-gray-200 bg-white p-2">
            <div className="flex items-center justify-between">
              <Typography.Text strong className="text-sm">
                {ingredient.name}
              </Typography.Text>
              <Typography.Text className="shrink-0 text-sm">{ingredient.portion} g</Typography.Text>
            </div>
            <div className="flex items-center justify-between">
              {hasMeasure ? (
                <Typography.Text type="secondary" className="text-xs">
                  {multiplier} x {ingredient.measure}
                </Typography.Text>
              ) : (
                <Typography.Text type="secondary" className="text-xs">
                  -
                </Typography.Text>
              )}
              <Typography.Text type="secondary" className="shrink-0 text-xs">
                {enhancedIngredient.portionCalories} kcal
              </Typography.Text>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const getStatusBadge = (status: AiAgentRequest["status"], runTaskStatus?: RunStatus) => {
  if (runTaskStatus === "CANCELED") {
    return <Badge status="default" text="Anulowany" />;
  }

  if (runTaskStatus === "EXECUTING") {
    return <Badge status="processing" text="W trakcie" />;
  }

  if (runTaskStatus === "COMPLETED" && status !== "COMPLETED") {
    return <Badge status="processing" text="Do oceny" />;
  }

  switch (status) {
    case "STARTED":
      return <Badge status="processing" text="Uruchomiony" />;
    case "TO_EVALUATE":
      return <Badge status="processing" text="Do oceny" />;
    case "COMPLETED":
      return <Badge status="success" text="Zakończony" />;
    case "CANCELLED":
      return <Badge status="default" text="Anulowany" />;
    case "FAILED":
      return <Badge status="error" text="Błąd" />;
    default:
      return <Badge status="default" text="Zaplanowany" />;
  }
};

const getTaskStatusBadge = (status: string, t: TFunction) => {
  const getTranslatedStatus = (status: string) => {
    // Map some legacy statuses to the standard ones
    const statusMap: Record<string, string> = {
      STARTED: "EXECUTING",
      RUNNING: "EXECUTING",
      ERRORED: "FAILED",
      SKIPPED: "SKIPPED",
    };
    const mappedStatus = statusMap[status] || status;
    return t(`taskStatus.${mappedStatus}`, { defaultValue: status });
  };

  switch (status) {
    case "EXECUTING":
    case "RUNNING":
      return <Badge status="processing" text={getTranslatedStatus(status)} />;
    case "COMPLETED":
      return <Badge status="success" text={getTranslatedStatus(status)} />;
    case "SKIPPED":
      return <Badge status="default" text={getTranslatedStatus(status)} />;
    case "FAILED":
    case "ERRORED":
      return <Badge status="error" text={getTranslatedStatus(status)} />;
    case "CANCELED":
    case "CANCELLED":
      return <Badge status="default" text={getTranslatedStatus(status)} />;
    case "WAITING":
    case "QUEUED":
      return <Badge status="warning" text={getTranslatedStatus(status)} />;
    default:
      return <Badge status="default" text={getTranslatedStatus(status)} />;
  }
};

const getOperationTitle = (operationType: string, t: TFunction) => {
  return t(`operations.${operationType}`, { defaultValue: operationType });
};

const renderExecutingProgress = (run: TaskRun, t: TFunction) => {
  const progressData = run.metadata;
  if (!progressData || typeof progressData !== "object") return null;

  const { steps, finishedSteps, operationType } = progressData;

  if (typeof steps !== "number" || typeof finishedSteps !== "number") return null;

  const progressPercent = Math.round((finishedSteps / steps) * 100);

  return (
    <Card size="small" className="mb-3 border-blue-200 bg-blue-50">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Typography.Text strong className="text-blue-800">
            {t("progress.processing")}
          </Typography.Text>
          <Badge status="processing" text={t("progress.inProgress")} />
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <Typography.Text className="text-sm text-blue-700">
              {t("progress.currentStep")}:{" "}
              {operationType ? getOperationTitle(String(operationType), t) : t("progress.processing")}
            </Typography.Text>
            <Typography.Text className="text-xs text-blue-600">
              {t("progress.stepsCounter", {
                finished: finishedSteps,
                total: steps,
              })}
            </Typography.Text>
          </div>
          <Progress
            percent={progressPercent}
            size="small"
            status="active"
            strokeColor={{
              "0%": "#1890ff",
              "100%": "#52c41a",
            }}
          />
        </div>
      </div>
    </Card>
  );
};

type StepDecision = "accepted" | "rejected" | "pending";

type StepDecisions = Record<string, Record<string, StepDecision>>;

const renderHistoryStep = (
  step: DishWorkflowSchemaType["history"][number],
  index: number,
  t: TFunction,
  runId: string,
  stepDecisions: StepDecisions,
  onAccept: (runId: string, stepId: string) => void,
  onReject: (runId: string, stepId: string) => void,
  isRequestFinished: boolean,
) => {
  const getStepTitle = (stepId: string) => {
    return t(`operations.${stepId}`, { defaultValue: stepId });
  };

  const toDisplayString = (item: unknown): string => {
    if (item === null || item === undefined) return "";
    if (typeof item === "string" || typeof item === "number") return String(item);
    if (typeof item === "object" && item !== null) {
      const obj = item as Record<string, unknown>;
      if (typeof obj.name === "string") return obj.name;
      if (obj.id !== undefined) return String(obj.id);
    }
    return String(item);
  };

  const renderUpdatedValue = (key: string, value: unknown, step: DishWorkflowSchemaType["history"][number]) => {
    if (step.stepId === "set-dish-types" && Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((item, idx) => (
            <Tag key={idx} color="blue" className="whitespace-pre-wrap">
              {t(`nutrition:dishType.${toDisplayString(item)}`)}
            </Tag>
          ))}
        </div>
      );
    }

    if (step.stepId === "fresh-produce-quantity" && Array.isArray(value)) {
      return <UpdatedIngredientsList ingredients={value as DishIngredient[]} />;
    }

    if (step.stepId === "check-includes" && Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((item, idx) => (
            <Tag key={idx} color="blue" className="whitespace-pre-wrap">
              {t(`nutrition:sensitiveIngredient.${toDisplayString(item)}`)}
            </Tag>
          ))}
        </div>
      );
    }

    if (step.stepId === "match-ingredients-to-preparation" && Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((item, idx) => (
            <Typography.Text key={idx} className="text-xs italic">
              {idx + 1}. {toDisplayString(item)}
            </Typography.Text>
          ))}
        </div>
      );
    }

    if (Array.isArray(value)) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((item, idx) => (
            <Tag key={idx} color="blue" className="whitespace-pre-wrap">
              {toDisplayString(item)}
            </Tag>
          ))}
        </div>
      );
    }
    return <Typography.Text strong>{String(value)}</Typography.Text>;
  };

  const currentDecision = stepDecisions[runId]?.[step.stepId] || "pending";
  const isAccepted = currentDecision === "accepted";
  const isRejected = currentDecision === "rejected";

  return (
    <Card
      key={`${step.stepId}-${index}`}
      size="small"
      className={`mb-3 ${isAccepted ? "border-green-300 bg-green-50" : isRejected ? "border-red-300 bg-red-50" : ""}`}
      title={
        <div className="flex  items-center justify-between">
          <span className="basis-1/2 whitespace-pre-wrap text-sm font-medium">{getStepTitle(step.stepId)}</span>

          <div className={cn("flex basis-1/2 items-center justify-end gap-2", isRequestFinished && "hidden")}>
            <Button
              type={isAccepted ? "primary" : "default"}
              icon={<CheckOutlined className="text-green-500" />}
              size="small"
              className={isAccepted ? "bg-green-500 hover:bg-green-600" : ""}
              onClick={() => onAccept(runId, step.stepId)}
              disabled={isAccepted}
            >
              {isAccepted ? "Zaakceptowano" : "Akceptuj"}
            </Button>
            <Button
              danger={isRejected}
              type={isRejected ? "primary" : "default"}
              icon={<CloseOutlined className="text-red-500" />}
              size="small"
              onClick={() => onReject(runId, step.stepId)}
              disabled={isRejected}
            >
              {isRejected ? "Odrzucono" : "Odrzuć"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div>
          <Typography.Text className="mb-1 block text-xs text-gray-600">Powód zmiany:</Typography.Text>
          <Typography.Text className="text-sm">{step.reason}</Typography.Text>
        </div>

        {step.updated && Object.keys(step.updated).length > 0 && (
          <div>
            <Typography.Text className="mb-2 block text-xs text-gray-600">Wprowadzone zmiany:</Typography.Text>
            <div className="space-y-2">
              {Object.entries(step.updated).map(([key, value]) => (
                <div key={key} className="flex flex-wrap items-center gap-2">
                  <Typography.Text className="min-w-fit text-xs text-gray-500">
                    {t(`nutrition:changes.${key}`, { defaultValue: key })}:
                  </Typography.Text>
                  {renderUpdatedValue(key, value, step)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

const getHistory = (task: TaskRun) => {
  if (
    task.status === "COMPLETED" &&
    task.output?.data.status === "success" &&
    task.output?.data?.result &&
    "history" in task.output.data.result
  ) {
    return task.output.data.result.history || [];
  }
  return [];
};

const getHistoryFromFallback = (task: BackgroundTask): DishWorkflowSchemaType["history"] => {
  if (
    task.status === "COMPLETED" &&
    task.result?.data?.status === "success" &&
    task.result?.data?.result &&
    "history" in task.result.data.result
  ) {
    return task.result.data.result.history || [];
  }
  return [];
};

type UnifiedTask = {
  id: string;
  taskIdentifier: string;
  status: RunStatus;
  type: "dish-issue" | "sales-call-analysis";
  metadata: {
    dishId?: string;
    issueId?: string;
    dishTitle?: string;
    fileName?: string;
    recordedAt?: string | null;
    teamMemberId?: string;
  };
  createdAt: Date;
  updatedAt: Date;
  history: DishWorkflowSchemaType["history"];
  result?: SalesCallTaskResult | null;
  isFallback: boolean;
};

const mapFallbackToUnified = (task: BackgroundTask): UnifiedTask => ({
  id: task.id,
  taskIdentifier: task.type || "dish-issue-task",
  status: task.status === "STARTED" ? "EXECUTING" : (task.status as RunStatus),
  type: task.type === "sales-call-analysis-task" ? "sales-call-analysis" : "dish-issue",
  metadata: {
    dishId: task.data?.dishId,
    issueId: task.id,
    dishTitle: task.data?.dishTitle,
    fileName: task.data?.fileName,
    recordedAt: task.data?.recordedAt,
    teamMemberId: task.data?.teamMemberId,
  },
  createdAt: new Date(task.createdAt),
  updatedAt: new Date(task.updatedAt),
  history: getHistoryFromFallback(task),
  result: (task.result?.data as SalesCallTaskResult | undefined) ?? null,
  isFallback: true,
});

const mapRunToUnified = (run: TaskRun): UnifiedTask => ({
  id: run.id,
  taskIdentifier: run.taskIdentifier,
  status: run.status as RunStatus,
  type: "dish-issue",
  metadata: {
    dishId: run.metadata?.dishId ? String(run.metadata.dishId) : undefined,
    issueId: run.metadata?.issueId ? String(run.metadata.issueId) : undefined,
    dishTitle: run.metadata?.dishTitle ? String(run.metadata.dishTitle) : undefined,
  },
  createdAt: run.createdAt,
  updatedAt: run.updatedAt,
  history: getHistory(run),
  isFallback: false,
});

const mapSalesRunToUnified = (run: TaskRun): UnifiedTask => ({
  id: run.id,
  taskIdentifier: run.taskIdentifier,
  status: run.status as RunStatus,
  type: "sales-call-analysis",
  metadata: {
    fileName: run.metadata?.fileName ? String(run.metadata.fileName) : undefined,
    recordedAt: run.metadata?.recordedAt ? String(run.metadata.recordedAt) : undefined,
    teamMemberId: run.metadata?.teamMemberId ? String(run.metadata.teamMemberId) : undefined,
  },
  createdAt: run.createdAt,
  updatedAt: run.updatedAt,
  history: [],
  result: (run.output as SalesCallTaskResult | undefined) ?? null,
  isFallback: false,
});

const getRequestRecordings = (request: AiAgentRequest): RequestRecording[] => {
  const data = request.data as { recordings?: RequestRecording[] } | null;
  return Array.isArray(data?.recordings) ? data.recordings : [];
};

const mapRequestRecordingToUnified = (recording: RequestRecording, request: AiAgentRequest): UnifiedTask => ({
  id: recording.taskId,
  taskIdentifier: "sales-call-analysis-task",
  status: "QUEUED",
  type: "sales-call-analysis",
  metadata: {
    fileName: recording.fileName,
    recordedAt: recording.recordedAt,
  },
  createdAt: new Date(request.createdAt),
  updatedAt: new Date(request.updatedAt ?? request.createdAt),
  history: [],
  result: {
    status: recording.status ?? "PENDING",
    fileName: recording.fileName,
    recordedAt: recording.recordedAt,
  },
  isFallback: true,
});

const getAgentTitle = (agentType: string, t: TFunction) => t(`agents.title.${agentType}`, { defaultValue: agentType });

const formatDurationSeconds = (seconds: number) => {
  const totalSeconds = Math.max(0, Math.round(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours} godz ${minutes.toString().padStart(2, "0")} min ${remainingSeconds.toString().padStart(2, "0")} s`;
  }

  if (minutes > 0) {
    return `${minutes} min ${remainingSeconds.toString().padStart(2, "0")} s`;
  }

  return `${remainingSeconds} s`;
};

const renderSalesCallTaskDetails = (task: UnifiedTask, t: TFunction) => {
  const result = task.result;
  const fileName = result?.fileName ?? task.metadata.fileName ?? "Nagranie rozmowy";
  const recordedAt = result?.recordedAt ?? task.metadata.recordedAt;
  const durationSeconds = typeof result?.durationSeconds === "number" ? Math.round(result.durationSeconds) : null;
  const score = result?.analysis?.score;
  const transcript = result?.transcript?.trim();

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <FileTextOutlined className="shrink-0 text-slate-500" />
            <Typography.Text strong className="truncate">
              {fileName}
            </Typography.Text>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            <span>Utworzony: {dayjs(task.createdAt).format("DD.MM.YYYY HH:mm:ss")}</span>
            <span>Aktualizacja: {dayjs(task.updatedAt).format("DD.MM.YYYY HH:mm:ss")}</span>
            {recordedAt ? <span>Nagranie: {dayjs(recordedAt).format("DD.MM.YYYY HH:mm:ss")}</span> : null}
            {durationSeconds !== null ? <span>Długość: {formatDurationSeconds(durationSeconds)}</span> : null}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-start gap-1 md:items-end">
          {getTaskStatusBadge(task.status, t)}
          {typeof score === "number" ? <Tag color="green">Ocena {Math.round(score)}/100</Tag> : null}
        </div>
      </div>

      {result?.analysis?.summary ? (
        <Typography.Paragraph className="!mb-0 !mt-3 text-sm text-slate-700">
          {result.analysis.summary}
        </Typography.Paragraph>
      ) : null}

      {result?.meetingId ? (
        <Typography.Text className="mt-2 block text-xs text-gray-500">
          Dopasowane spotkanie: {result.meetingId}
        </Typography.Text>
      ) : null}

      {result?.status === "unmatched" ? (
        <Alert className="mt-3" type="warning" showIcon message="Nie dopasowano nagrania do spotkania Calendly" />
      ) : null}

      {transcript ? (
        <div className="mt-3 rounded-md border border-slate-100 bg-slate-50 p-3">
          <Typography.Text className="mb-2 block text-xs font-medium uppercase text-slate-500">
            Transkrypcja
          </Typography.Text>
          <Typography.Paragraph className="!mb-0 max-h-64 overflow-y-auto whitespace-pre-wrap text-sm text-slate-700">
            {transcript}
          </Typography.Paragraph>
        </div>
      ) : null}

      {result?.status === "skipped_short" || task.status === "SKIPPED" ? (
        <Alert className="mt-3" type="info" showIcon message="Nagranie pominięte, bo było krótsze niż 30 sekund" />
      ) : null}

      {result?.error ? <Alert className="mt-3" type="error" showIcon message={result.error} /> : null}
    </div>
  );
};

const getNutritionPlanIdFromRun = (run?: RealtimeRun<any>) => {
  const output = run?.output as
    | {
        data?: {
          status?: string;
          planId?: string;
          result?: {
            planId?: string;
          };
        };
      }
    | undefined;

  return output?.data?.planId || (output?.data?.status === "success" ? output.data.result?.planId : undefined);
};

const getNutritionProgress = (run?: RealtimeRun<any>) => {
  const metadata = run?.metadata;
  if (!metadata || typeof metadata !== "object") return null;

  const steps = typeof metadata.steps === "number" ? metadata.steps : undefined;
  const finishedSteps = typeof metadata.finishedSteps === "number" ? metadata.finishedSteps : undefined;
  const operationType = typeof metadata.operationType === "string" ? metadata.operationType : undefined;
  const planId = typeof metadata.planId === "string" ? metadata.planId : undefined;

  return { steps, finishedSteps, operationType, planId };
};

const getStoredNutritionStepCount = (request: AiAgentRequest) => {
  const steps = request.data?.result?.timings?.steps ?? request.data?.timings?.steps;
  if (!Array.isArray(steps)) return undefined;

  return steps.length || undefined;
};

const getNumberValue = (value: unknown) => (typeof value === "number" && Number.isFinite(value) ? value : undefined);

const NUTRITION_RUN_FINISHED_STATUSES = new Set<RunStatus>([
  "COMPLETED",
  "CANCELED",
  "FAILED",
  "CRASHED",
  "SYSTEM_FAILURE",
  "EXPIRED",
  "TIMED_OUT",
]);

const formatDurationMs = (durationMs?: number) => {
  if (!durationMs || durationMs < 0) return null;

  const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes <= 0) return `${seconds} s`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours > 0) {
    return `${hours} h ${remainingMinutes} min ${seconds} s`;
  }

  return `${minutes} min ${seconds} s`;
};

const getNutritionGenerationTimeMs = (request: AiAgentRequest, run?: RealtimeRun<any>) => {
  const runOutput = run?.output as
    | {
        time?: unknown;
        timings?: { totalMs?: unknown };
        data?: {
          time?: unknown;
          timings?: { totalMs?: unknown };
          result?: {
            time?: unknown;
            timings?: { totalMs?: unknown };
          };
        };
      }
    | undefined;
  const runMetadata = run?.metadata as { timings?: { totalMs?: unknown } } | undefined;

  return (
    getNumberValue(request.data?.result?.time) ??
    getNumberValue(request.data?.result?.timings?.totalMs) ??
    getNumberValue(request.data?.timings?.totalMs) ??
    getNumberValue(runOutput?.time) ??
    getNumberValue(runOutput?.timings?.totalMs) ??
    getNumberValue(runOutput?.data?.time) ??
    getNumberValue(runOutput?.data?.timings?.totalMs) ??
    getNumberValue(runOutput?.data?.result?.time) ??
    getNumberValue(runOutput?.data?.result?.timings?.totalMs) ??
    getNumberValue(runMetadata?.timings?.totalMs)
  );
};

const getDateMs = (value: unknown) => {
  if (!value) return undefined;

  const dateMs = new Date(value as string | number | Date).getTime();
  return Number.isFinite(dateMs) ? dateMs : undefined;
};

const getNutritionGenerationFallbackTimeMs = (request: AiAgentRequest, run?: RealtimeRun<any>) => {
  const runDates = run as
    | {
        startedAt?: unknown;
        createdAt?: unknown;
        completedAt?: unknown;
        finishedAt?: unknown;
        updatedAt?: unknown;
      }
    | undefined;
  const requestResult = request.data?.result as { completedAt?: unknown; failedAt?: unknown } | undefined;
  const startMs = getDateMs(runDates?.startedAt) ?? getDateMs(runDates?.createdAt) ?? getDateMs(request.createdAt);
  const endMs =
    getDateMs(runDates?.completedAt) ??
    getDateMs(runDates?.finishedAt) ??
    getDateMs(requestResult?.completedAt) ??
    getDateMs(requestResult?.failedAt) ??
    getDateMs(request.updatedAt);

  if (!startMs || !endMs || endMs < startMs) return undefined;

  return endMs - startMs;
};

type NutritionGeneratorDto = {
  name?: string;
  prompt?: string;
  generatorVersion?: "v1" | "v2" | "v3";
  dietaryPreference?: string | number;
  exclusions?: string[] | null;
  mealsSchema?: string | number;
  macroSplit?: string;
  targetCalories?: number;
  targets?: {
    carbs?: number;
    protein?: number;
    fat?: number;
  };
  model?: string;
  copySupplementsPlan?: boolean;
};

const dietaryExclusionsLabels: Record<string, string> = {
  lactose: "Laktoza",
  casein: "Kazeina",
  lowFoodmap: "Low FODMAP",
  gluten: "Gluten",
};

const nutritionGeneratorVersionLabels: Record<NonNullable<NutritionGeneratorDto["generatorVersion"]>, string> = {
  v1: "Obecny generator",
  v2: "Generator V2 - historyczne diety + walidacja",
  v3: "Generator V3 - makro + wykluczenia + skalowanie porcji",
};

const renderNutritionGeneratorContext = ({
  dto,
  trainee,
  traineeId,
  author,
  isOwnerAuthored,
  t,
}: {
  dto: NutritionGeneratorDto;
  trainee?: { firstName: string | null; lastName: string | null; email: string | null } | null;
  traineeId?: string;
  author?: { firstName: string | null; lastName: string | null; email: string | null } | null;
  isOwnerAuthored?: boolean;
  t: TFunction;
}) => {
  const traineeName = [trainee?.firstName, trainee?.lastName].filter(Boolean).join(" ");
  const displayTrainee = traineeName || trainee?.email || traineeId || "-";
  const authorName = [author?.firstName, author?.lastName].filter(Boolean).join(" ");
  // Historical owner requests can have no trainerId; userId still identifies their author.
  const displayAuthor = authorName || author?.email || (isOwnerAuthored ? "Właściciel konta" : "-");
  const dietaryPreference = dto.dietaryPreference
    ? t(`nutrition:dietaryPreferenceEnum.${dto.dietaryPreference}`, { defaultValue: String(dto.dietaryPreference) })
    : "-";
  const macroSplit = dto.macroSplit
    ? t(`nutrition:macroSplitDetailedShort.${dto.macroSplit}`, { defaultValue: dto.macroSplit })
    : "-";
  const exclusions = dto.exclusions?.length
    ? dto.exclusions.map((item) => dietaryExclusionsLabels[item] || item).join(", ")
    : "Brak";

  const items = [
    {
      label: "Wersja",
      value: nutritionGeneratorVersionLabels[dto.generatorVersion ?? "v1"],
    },
    { label: "Klient", value: displayTrainee },
    // gender-neutral label — team members are of mixed genders, "Zlecił" would misgender half of them
    { label: "Zlecone przez", value: displayAuthor },
    { label: "Nazwa", value: dto.name || "-" },
    { label: "Kalorie", value: dto.targetCalories ? `${dto.targetCalories} kcal` : "-" },
    { label: "Posiłki", value: dto.mealsSchema ? `${dto.mealsSchema} dziennie` : "-" },
    { label: "Typ diety", value: dietaryPreference },
    { label: "Makro", value: macroSplit },
    {
      label: "B/W/T",
      value:
        dto.targets?.protein || dto.targets?.carbs || dto.targets?.fat
          ? `${dto.targets?.protein ?? "-"} / ${dto.targets?.carbs ?? "-"} / ${dto.targets?.fat ?? "-"}%`
          : "-",
    },
    { label: "Wykluczenia", value: exclusions },
    { label: "Suplementacja", value: dto.copySupplementsPlan ? "Kopiuj z aktualnej diety" : "Nie kopiuj" },
    { label: "Model", value: dto.model || "-" },
  ];

  return (
    <Card size="small" className="border-slate-200 bg-white">
      <div className="space-y-3">
        <Typography.Text strong>Kontekst generowania</Typography.Text>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {items.map((item) => (
            <div key={item.label} className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
              <Typography.Text className="block text-xs text-gray-500">{item.label}</Typography.Text>
              <Typography.Text className="text-sm font-medium">{item.value}</Typography.Text>
            </div>
          ))}
        </div>
        {dto.prompt ? (
          <div className="rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
            <Typography.Text className="block text-xs text-gray-500">Instrukcja trenera</Typography.Text>
            <Typography.Text className="whitespace-pre-wrap text-sm">{dto.prompt}</Typography.Text>
          </div>
        ) : null}
      </div>
    </Card>
  );
};

const NutritionGeneratorRequest = ({ request }: { request: AiAgentRequest }) => {
  const { t } = useTranslation(["dashboard", "nutrition"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { closeDrawer } = useBackgroundTasks();
  const { deleteDishTaskMutation } = useDishTaskSave();
  const { cancelAgentRequestMutation } = useBackgroundTasksQuery({ enabled: false });
  const isRequestFinished =
    request.status === "COMPLETED" ||
    request.status === "FAILED" ||
    request.status === "CANCELLED" ||
    request.status === "DELETED";
  const isStoredGenerationDone = isRequestFinished || request.status === "TO_EVALUATE";

  const { runs, error } = useRealtimeRunsWithTag<any>(request.metadata?.tags || [], {
    enabled: Boolean(request.metadata?.accessToken && !isStoredGenerationDone),
    accessToken: request.metadata?.accessToken,
  });
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [isMarkingCompleted, setIsMarkingCompleted] = useState(false);

  const traineeId = request.data?.traineeId;
  const dto = (request.data?.dto || {}) as NutritionGeneratorDto;
  const expectedTaskIdentifier =
    dto.generatorVersion === "v3"
      ? "generate-nutrition-plan-v3-task"
      : dto.generatorVersion === "v2"
        ? "generate-nutrition-plan-v2-task"
        : "generate-nutrition-plan-task";
  const run = runs?.find(
    (item) =>
      item.taskIdentifier === expectedTaskIdentifier ||
      item.taskIdentifier === "generate-nutrition-plan-task" ||
      item.taskIdentifier === "generate-nutrition-plan-v2-task" ||
      item.taskIdentifier === "generate-nutrition-plan-v3-task",
  ) as RealtimeRun<any> | undefined;
  const progress = getNutritionProgress(run);
  const planId = getNutritionPlanIdFromRun(run) || progress?.planId || request.data?.result?.planId;
  const savedGenerationTimeMs =
    getNutritionGenerationTimeMs(request, run) ?? getNutritionGenerationFallbackTimeMs(request, run);
  const isRunFinished = run?.status ? NUTRITION_RUN_FINISHED_STATUSES.has(run.status as RunStatus) : false;
  // TO_EVALUATE means the generation itself already finished and is awaiting trainer review
  const isGenerationDone = isStoredGenerationDone || isRunFinished;

  useEffect(() => {
    if (isGenerationDone) return;

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isGenerationDone]);

  // While running, ALWAYS tick from the run start timestamp — saved timings may carry totals
  // from a previous failed attempt (trigger.dev retries keep run metadata) and must not freeze
  // the counter. The saved value is only authoritative once the generation is done.
  const generationStartTs = run?.startedAt ?? run?.createdAt ?? request.createdAt;
  const displayedGenerationTime = isGenerationDone
    ? formatDurationMs(savedGenerationTimeMs)
    : generationStartTs
      ? formatDurationMs(Math.max(1000, nowMs - new Date(generationStartTs).getTime()))
      : null;
  const traineeQuery = useQuery({
    queryKey: ["nutritionGeneratorTraineeContext", traineeId],
    enabled: Boolean(traineeId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trainee")
        .select("firstName, lastName, email")
        .eq("id", traineeId!)
        .maybeSingle();

      if (error) throw new Error(error.message);
      return data;
    },
  });
  // trainerId is the real membership that triggered the generation. Historical
  // owner rows can still be null and fall back to the request userId label.
  const authorQuery = useQuery({
    queryKey: ["nutritionGeneratorAuthorContext", request.trainerId],
    enabled: Boolean(request.trainerId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_member")
        .select("firstName, lastName, email")
        .eq("id", request.trainerId!)
        .maybeSingle();

      if (error) throw new Error(error.message);
      return data;
    },
  });
  const canOpenPlan = Boolean(planId && traineeId);
  const isGeneratedSuccessfully = request.status === "TO_EVALUATE" || request.status === "COMPLETED";
  const defaultSteps = dto.generatorVersion === "v3" ? 11 : dto.generatorVersion === "v2" ? 10 : 5;
  const steps = progress?.steps ?? getStoredNutritionStepCount(request) ?? defaultSteps;
  const finishedSteps = progress?.finishedSteps ?? (isGeneratedSuccessfully ? steps : 0);
  const progressPercent = Math.min(100, Math.round((finishedSteps / steps) * 100));
  const operationTitle = progress?.operationType
    ? getOperationTitle(progress.operationType, t)
    : isGeneratedSuccessfully
      ? getOperationTitle("completed", t)
      : request.status === "FAILED"
        ? getOperationTitle("failed", t)
        : request.status === "CANCELLED"
          ? "Anulowano generowanie"
          : "Oczekiwanie w kolejce";
  const canCancelTask =
    !isRequestFinished &&
    request.status !== "TO_EVALUATE" &&
    (!run || ["PENDING_VERSION", "QUEUED", "DEQUEUED", "EXECUTING", "WAITING", "DELAYED"].includes(run.status));
  const canDeleteTask =
    request.status === "TO_EVALUATE" ||
    request.status === "FAILED" ||
    request.status === "COMPLETED" ||
    request.status === "CANCELLED";

  const markRequestAsCompleted = async () => {
    await supabase
      .from("ai_agent_request")
      .update({
        status: "COMPLETED",
        updatedAt: new Date().toISOString(),
      })
      .eq("id", request.id)
      .select("id")
      .single()
      .throwOnError();

    await queryClient.invalidateQueries({ queryKey: ["ai_agent_request"] });
  };

  const openGeneratedPlan = async () => {
    if (!planId || !traineeId) return;

    if (request.status === "TO_EVALUATE") {
      await markRequestAsCompleted();
    }

    navigate(`/trainee/details/${traineeId}?template=true&planId=${planId}&tab=nutrition&nutritionTab=schedule`);
    closeDrawer();
  };

  const handleMarkAsCompleted = async () => {
    setIsMarkingCompleted(true);
    try {
      await markRequestAsCompleted();
    } finally {
      setIsMarkingCompleted(false);
    }
  };

  const handleDelete = () => {
    deleteDishTaskMutation.mutate({ requestId: request.id });
  };

  const handleCancel = () => {
    cancelAgentRequestMutation.mutate({ requestId: request.id });
  };

  const collapseItems = [
    {
      key: request.id,
      label: (
        <div className="flex w-full items-center justify-between gap-3">
          <span className="font-medium">
            [
            {dto.generatorVersion === "v3"
              ? "Generator diety V3"
              : dto.generatorVersion === "v2"
                ? "Generator diety V2"
                : "Generator diety"}
            ] {dayjs(request.createdAt).format("DD.MM.YYYY HH:mm")}
          </span>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {getStatusBadge(request.status, run?.status as RunStatus | undefined)}
            {canCancelTask && (
              <Popconfirm
                title="Anulować generowanie?"
                description="Zadanie zostanie zatrzymane w Trigger.dev i oznaczone jako anulowane."
                onConfirm={handleCancel}
                okText="Tak, anuluj"
                cancelText="Nie"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<CloseOutlined />} loading={cancelAgentRequestMutation.isLoading}>
                  Anuluj
                </Button>
              </Popconfirm>
            )}
            {request.status === "TO_EVALUATE" && (
              <Popconfirm
                title="Oznaczyć jako zakończone?"
                description="Zadanie zostanie oznaczone jako zakończone bez otwierania planu."
                onConfirm={() => void handleMarkAsCompleted()}
                okText="Tak, oznacz"
                cancelText="Anuluj"
              >
                <Button type="primary" ghost icon={<CheckOutlined />} loading={isMarkingCompleted} />
              </Popconfirm>
            )}
            {canDeleteTask && (
              <Popconfirm
                title="Usunąć zadanie?"
                description="Czy na pewno chcesz usunąć to zadanie?"
                onConfirm={handleDelete}
                okText="Tak, usuń"
                cancelText="Anuluj"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<DeleteOutlined />} loading={deleteDishTaskMutation.isLoading} />
              </Popconfirm>
            )}
          </div>
        </div>
      ),
      children: (
        <div className="space-y-4">
          {error && <Alert message={error.message} type="error" />}

          {renderNutritionGeneratorContext({
            dto,
            trainee: traineeQuery.data,
            traineeId,
            author: authorQuery.data,
            isOwnerAuthored: !request.trainerId,
            t,
          })}

          <Card size="small" className="border-blue-200 bg-blue-50">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Typography.Text strong className="text-blue-800">
                  Tworzenie jadłospisu
                </Typography.Text>
                {getTaskStatusBadge((run?.status as string | undefined) || request.status, t)}
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <Typography.Text className="text-sm text-blue-700">Aktualny krok: {operationTitle}</Typography.Text>
                  <Typography.Text className="shrink-0 text-xs text-blue-600">
                    {finishedSteps}/{steps}
                  </Typography.Text>
                </div>
                <Progress
                  percent={progressPercent}
                  size="small"
                  status={
                    request.status === "FAILED"
                      ? "exception"
                      : request.status === "CANCELLED"
                        ? "normal"
                        : progressPercent === 100
                          ? "success"
                          : "active"
                  }
                  strokeColor={{ "0%": "#1890ff", "100%": "#52c41a" }}
                />
              </div>
              {displayedGenerationTime ? (
                <div className="flex items-center justify-between rounded-md border border-blue-100 bg-white/70 px-3 py-2">
                  <Typography.Text className="text-xs text-blue-700">
                    {isGenerationDone ? "Czas generowania" : "Czas od uruchomienia"}
                  </Typography.Text>
                  <Typography.Text className="text-sm font-medium text-blue-900">
                    {displayedGenerationTime}
                  </Typography.Text>
                </div>
              ) : null}
            </div>
          </Card>

          {(() => {
            const result = request.data?.result as
              | {
                  adjustments?: string[];
                  adjustmentsDetails?: AuditAdjustment[];
                  warnings?: string[];
                  warningsDetails?: AuditWarning[];
                }
              | undefined;
            const adjustments = Array.isArray(result?.adjustments) ? result.adjustments : [];
            const adjustmentsDetails = Array.isArray(result?.adjustmentsDetails) ? result.adjustmentsDetails : [];
            const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
            const warningsDetails = Array.isArray(result?.warningsDetails) ? result.warningsDetails : [];
            // older requests carry only the raw strings — render them as before
            const hasStructured = adjustmentsDetails.length > 0 || warningsDetails.length > 0;
            if (!hasStructured && adjustments.length === 0 && warnings.length === 0) return null;

            const adjustmentsByDay = groupBy(adjustmentsDetails, (item) => item.day);
            const dayOrder = Object.keys(AUDIT_DAY_LABELS).filter((day) => adjustmentsByDay[day]?.length);
            const adjustmentsCount = hasStructured ? adjustmentsDetails.length : adjustments.length;
            const warningsCount = hasStructured ? warningsDetails.length : warnings.length;

            const renderAdjustment = (item: AuditAdjustment, index: number) => (
              <li key={index} className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 py-1">
                <span className="w-24 shrink-0 text-gray-500">{AUDIT_MEAL_LABELS[item.mealType] ?? item.mealType}</span>
                {item.kind === "swap" ? (
                  <>
                    <Tag color="blue" className="m-0">
                      zamiana
                    </Tag>
                    <span className="text-gray-400 line-through">{item.fromName}</span>
                    <span className="text-gray-400">→</span>
                    <span className="font-medium">{item.dishName}</span>
                    <span className="text-gray-500">
                      ({item.fromKcal} → {item.toKcal} kcal)
                    </span>
                    {item.reason ? (
                      <Tag color="gold" className="m-0">
                        {AUDIT_REASON_LABELS[item.reason] ?? item.reason}
                      </Tag>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Tag color={item.kind === "recipe" ? "purple" : "default"} className="m-0">
                      {item.kind === "recipe" ? "korekta składników" : `porcja ×${item.factor ?? "?"}`}
                    </Tag>
                    <span className="font-medium">{item.dishName}</span>
                    <span className="text-gray-500">
                      ({item.fromKcal} → {item.toKcal} kcal)
                    </span>
                  </>
                )}
              </li>
            );

            const renderWarning = (item: AuditWarning, index: number) => (
              <li key={index} className="flex flex-wrap items-center gap-x-1.5 py-0.5">
                {item.metric ? (
                  <>
                    <span className="font-medium">
                      {item.day ? `${AUDIT_DAY_LABELS[item.day] ?? item.day}: ` : ""}
                      {AUDIT_METRIC_LABELS[item.metric] ?? item.metric}
                    </span>
                    <span>
                      {item.value}
                      {item.metric === "kcal" ? " kcal" : " g"} przy celu {item.target}
                      {item.metric === "kcal" ? " kcal" : " g"}
                    </span>
                    <Tag color={item.level === "critical" ? "red" : "orange"} className="m-0">
                      {item.level === "critical" ? "poza tolerancją" : "blisko granicy"}
                    </Tag>
                  </>
                ) : (
                  <span>{item.note}</span>
                )}
              </li>
            );

            return (
              <Card size="small" className="border-amber-200 bg-amber-50/40">
                <div className="space-y-2">
                  <Typography.Text strong>Dostosowania planu ({adjustmentsCount})</Typography.Text>
                  {adjustmentsCount === 0 ? (
                    <Typography.Text type="secondary" className="text-xs">
                      Plan zapisany bez dostosowań po kompozycji.
                    </Typography.Text>
                  ) : hasStructured ? (
                    <div className="max-h-72 space-y-2 overflow-y-auto text-xs text-gray-700">
                      {dayOrder.map((day) => (
                        <div key={day}>
                          <Typography.Text className="text-xs font-semibold">{AUDIT_DAY_LABELS[day]}</Typography.Text>
                          <ul className="m-0 list-none p-0">{adjustmentsByDay[day]!.map(renderAdjustment)}</ul>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <ul className="m-0 max-h-64 list-disc overflow-y-auto pl-5 text-xs text-gray-700">
                      {adjustments.map((item, index) => (
                        <li key={index} className="py-0.5">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                  {warningsCount > 0 ? (
                    <div>
                      <Typography.Text className="text-xs font-medium text-amber-700">
                        Ostrzeżenia walidacji ({warningsCount})
                      </Typography.Text>
                      {hasStructured ? (
                        <ul className="m-0 max-h-40 list-none overflow-y-auto p-0 text-xs text-amber-800">
                          {warningsDetails.map(renderWarning)}
                        </ul>
                      ) : (
                        <ul className="m-0 max-h-40 list-disc overflow-y-auto pl-5 text-xs text-amber-800">
                          {warnings.map((item, index) => (
                            <li key={index} className="py-0.5">
                              {item}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : null}
                </div>
              </Card>
            );
          })()}

          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs text-gray-600">ID requestu</span>
              <Typography.Text code copyable={{ text: request.id }}>
                {request.id}
              </Typography.Text>
            </div>
            {request.data?.error ? <AiRequestErrorAlert error={request.data.error} /> : null}
            {request.status === "CANCELLED" ? (
              <Alert className="mt-3" type="info" message="Generowanie zostało anulowane" />
            ) : null}
            {canOpenPlan ? (
              <Button type="primary" className="mt-3" onClick={() => void openGeneratedPlan()}>
                Otwórz wygenerowany plan
              </Button>
            ) : (
              <Typography.Text type="secondary" className="mt-3 block text-sm">
                Po zakończeniu generowania tutaj pojawi się przejście do planu roboczego.
              </Typography.Text>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full space-y-3">
      <Collapse items={collapseItems} size="small" className="bg-transparent" />
    </div>
  );
};

const DishAgentRequest = ({ request }: { request: AiAgentRequest }) => {
  const [stepDecisions, setStepDecisions] = useState<StepDecisions>({});

  const { isTrainer } = useUserRole();
  const isRequestFinished = request.status === "COMPLETED" || request.status === "FAILED";

  const { runs, error } = useRealtimeRunsWithTag(request.metadata?.tags || [], {
    enabled: Boolean(request.metadata?.accessToken && !isRequestFinished),
    accessToken: request.metadata?.accessToken,
  });

  const isSalesCallAnalysisRequest = request.agentType === "sales_call_analysis";

  const { fallbackTasks } = useBackgroundTaskFallback({
    requestId: request.id,
    enabled: isSalesCallAnalysisRequest || isRequestFinished || (!error && runs?.length === 0),
  });

  const triggerDishTasks = runs?.filter((run) => run.taskIdentifier === "dish-issue-task") || [];
  const triggerSalesCallTasks = runs?.filter((run) => run.taskIdentifier === "sales-call-analysis-task") || [];
  const mainTask = runs?.find((run) => run.taskIdentifier === "batch-dish-issues-task");
  const salesCallMainTask = runs?.find((run) => run.taskIdentifier === "batch-sales-call-analysis-task");
  const { t } = useTranslation("dashboard");
  const { saveDishTaskMutation, deleteDishTaskMutation } = useDishTaskSave();

  // Use trigger.dev runs if available, otherwise use fallback data
  const unifiedTasks: UnifiedTask[] = useMemo(() => {
    if (isSalesCallAnalysisRequest) {
      if (triggerSalesCallTasks.length > 0) {
        return triggerSalesCallTasks.map((run) => mapSalesRunToUnified(run));
      }

      const salesFallbackTasks =
        fallbackTasks?.filter((task) => task.type === "sales-call-analysis-task").map(mapFallbackToUnified) ?? [];

      if (salesFallbackTasks.length > 0) {
        return salesFallbackTasks;
      }

      return getRequestRecordings(request).map((recording) => mapRequestRecordingToUnified(recording, request));
    }

    if (triggerDishTasks.length > 0) {
      return triggerDishTasks.map(mapRunToUnified);
    }
    if (fallbackTasks && fallbackTasks.length > 0) {
      return fallbackTasks.filter((task) => task.type === "dish-issue-task").map(mapFallbackToUnified);
    }
    return [];
  }, [isSalesCallAnalysisRequest, triggerSalesCallTasks, fallbackTasks, request, triggerDishTasks]);

  // Keep original dishIssuesTasks for backward compatibility with renderExecutingProgress
  const dishIssuesTasks = triggerDishTasks;

  const handleAccept = (runId: string, stepId: string) => {
    setStepDecisions((prev) => ({
      ...prev,
      [runId]: {
        ...prev[runId],
        [stepId]: "accepted",
      },
    }));
  };

  const handleReject = (runId: string, stepId: string) => {
    setStepDecisions((prev) => ({
      ...prev,
      [runId]: {
        ...prev[runId],
        [stepId]: "rejected",
      },
    }));
  };

  const allStepsResolved = useMemo(() => {
    const completedTasks = unifiedTasks.filter((task) => task.status === "COMPLETED");
    if (completedTasks.length === 0) return false;

    for (const task of completedTasks) {
      for (const step of task.history) {
        const decision = stepDecisions[task.id]?.[step.stepId];
        if (!decision || decision === "pending") {
          return false;
        }
      }
    }
    return true;
  }, [unifiedTasks, stepDecisions]);

  const canDeleteTask = useMemo(() => {
    // Check if all subtasks are COMPLETED and request status is TO_EVALUATE
    return (
      isRequestFinished ||
      (request.status === "TO_EVALUATE" &&
        (unifiedTasks.length > 0 || (request.taskCount > 0 && unifiedTasks.length === 0 && isTrainer)) &&
        unifiedTasks.every((task) => task.status === "COMPLETED" || task.status === "CANCELED"))
    );
  }, [request.status, unifiedTasks, isRequestFinished, isTrainer, request.taskCount]);

  const getAcceptedChanges = () => {
    const acceptedChanges: DishTaskSave[] = [];
    const completedTasks = unifiedTasks.filter((task) => task.status === "COMPLETED");

    for (const task of completedTasks) {
      for (const step of task.history) {
        const decision = stepDecisions[task.id]?.[step.stepId];
        if (decision === "accepted" && task.metadata?.dishId && task.metadata?.issueId) {
          acceptedChanges.push({
            taskId: task.id,
            stepId: step.stepId,
            changes: step.updated as Partial<Dish>,
            dishId: task.metadata.dishId,
            issueId: task.metadata.issueId,
          });
        }
      }
    }
    return groupBy(acceptedChanges, "dishId");
  };

  const handleSave = () => {
    const acceptedChanges = getAcceptedChanges();
    saveDishTaskMutation.mutate({ acceptedChanges, requestId: request.id });
  };

  const handleDelete = () => {
    deleteDishTaskMutation.mutate({ requestId: request.id });
  };

  const isFinished =
    request.status === "COMPLETED" ||
    request.status === "FAILED" ||
    request.status === "CANCELLED" ||
    request.status === "DELETED";

  const collapseItems = [
    {
      key: request.id,
      label: (
        <div className="flex w-full items-center justify-between">
          <span className="font-medium">
            [{getAgentTitle(request.agentType, t)}] {request.taskCount} zadania -{" "}
            {dayjs(request.createdAt).format("DD.MM.YYYY HH:mm")}
          </span>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {getStatusBadge(request.status, (mainTask?.status ?? salesCallMainTask?.status) as RunStatus | undefined)}
            {canDeleteTask && (
              <Popconfirm
                title="Usunąć zadanie?"
                description="Czy na pewno chcesz usunąć to zadanie?"
                onConfirm={handleDelete}
                okText="Tak, usuń"
                cancelText="Anuluj"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<DeleteOutlined />} loading={deleteDishTaskMutation.isLoading} />
              </Popconfirm>
            )}
          </div>
        </div>
      ),
      children: (
        <div className="flex flex-col gap-3">
          {unifiedTasks.length > 0 ? (
            <>
              {/* Save Operation Section - Always at top */}
              {(() => {
                const completedTasks = unifiedTasks.filter((task) => task.status === "COMPLETED");
                const totalSteps = completedTasks.reduce((acc, task) => acc + task.history.length, 0);

                const resolvedSteps = completedTasks.reduce((acc, task) => {
                  return (
                    acc +
                    task.history.filter((step) => {
                      const decision = stepDecisions[task.id]?.[step.stepId];
                      return decision && decision !== "pending";
                    }).length
                  );
                }, 0);

                if (totalSteps > 0 && !isFinished) {
                  return (
                    <div
                      className={`rounded-lg border p-4 ${
                        allStepsResolved ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <Typography.Text strong className={allStepsResolved ? "text-green-800" : "text-amber-800"}>
                            {allStepsResolved ? "Wszystkie zmiany zostały przejrzane" : "Wymagana decyzja użytkownika"}
                          </Typography.Text>
                          <div className={`mt-1 text-sm ${allStepsResolved ? "text-green-600" : "text-amber-600"}`}>
                            {allStepsResolved
                              ? "Możesz teraz zapisać zaakceptowane zmiany w potrawie"
                              : `Przejrzyj i zdecyduj o ${totalSteps - resolvedSteps} z ${totalSteps} zmian`}
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            Postęp: {resolvedSteps}/{totalSteps} zmian przejrzanych
                          </div>
                        </div>
                        <Space>
                          {allStepsResolved && (
                            <Button
                              type="primary"
                              icon={<SaveOutlined />}
                              loading={saveDishTaskMutation.isLoading}
                              onClick={handleSave}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              Zapisz zmiany
                            </Button>
                          )}
                        </Space>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {unifiedTasks.every((task) => task.type === "sales-call-analysis") && (
                <div className="space-y-2">
                  <Divider orientation="left" className="text-sm font-medium text-gray-700">
                    Nagrania ({unifiedTasks.length})
                  </Divider>
                  {unifiedTasks.map((task) => (
                    <div key={task.id}>{renderSalesCallTaskDetails(task, t)}</div>
                  ))}
                </div>
              )}

              {unifiedTasks.every((task) => task.history.length === 0) &&
                unifiedTasks.every((task) => task.type !== "sales-call-analysis") &&
                isFinished && (
                  <div className="rounded-lg bg-gray-50 py-3 text-center text-gray-500">
                    Zadanie zakończone - brak dostępnej historii zmian
                  </div>
                )}

              {unifiedTasks.map((task) => {
                if (task.type === "sales-call-analysis") {
                  return null;
                }

                const hasHistory = task.history.length > 0;
                const triggerRun = dishIssuesTasks.find((run) => run.id === task.id);

                if (isFinished && task.status === "COMPLETED" && !hasHistory) {
                  return null;
                }

                return (
                  <div
                    key={task.id}
                    className={`space-y-4 ${hasHistory ? "rounded-lg border-2 border-slate-200 bg-slate-50 p-4" : ""}`}
                  >
                    {hasHistory && (
                      <div className="mb-2 flex items-center gap-2">
                        <Badge status="processing" />
                        <Typography.Text className="text-sm font-medium text-slate-700">
                          {task.metadata?.dishTitle || "Zadanie z propozycjami zmian"}
                        </Typography.Text>
                      </div>
                    )}

                    {!task.isFallback &&
                      triggerRun &&
                      triggerRun.status === "EXECUTING" &&
                      renderExecutingProgress(triggerRun, t)}

                    <div
                      className={`flex items-center justify-between rounded-lg p-3 ${
                        hasHistory ? "border border-slate-200 bg-white" : "bg-gray-50"
                      }`}
                    >
                      <div className="flex flex-col gap-1">
                        <div className="flex gap-1">
                          <span className="whitespace-nowrap text-sm font-medium">ID:</span>
                          <Typography.Text
                            code
                            copyable={{ text: task.id }}
                            style={{ width: 100 }}
                            className="whitespace-nowrap"
                          >
                            {task.id.slice(-10)}
                          </Typography.Text>
                        </div>

                        <span className="text-xs text-gray-600">
                          Utworzony: {dayjs(task.createdAt).format("DD.MM.YYYY HH:mm:ss")}
                        </span>
                        <span className="text-xs text-gray-600">
                          Ostatnia aktualizacja: {dayjs(task.updatedAt).format("DD.MM.YYYY HH:mm:ss")}
                        </span>
                        {task.isFallback && <span className="text-xs text-blue-500">(Dane z kopii zapasowej)</span>}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {getTaskStatusBadge(task.status, t)}
                        {task.metadata?.dishTitle && (
                          <span className="text-xs text-gray-500">{task.metadata.dishTitle}</span>
                        )}
                      </div>
                    </div>

                    {hasHistory && (
                      <div className="space-y-2">
                        <Divider orientation="left" className="text-sm font-medium text-gray-700">
                          Historia zmian ({task.history.length})
                        </Divider>
                        {task.history.map((step, index: number) =>
                          renderHistoryStep(
                            step,
                            index,
                            t,
                            task.id,
                            stepDecisions,
                            handleAccept,
                            handleReject,
                            isRequestFinished,
                          ),
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            <div className="py-4 text-center text-gray-500">
              {request.status === "COMPLETED"
                ? "Historia zakończonych zadań jest obecnie niedostępna"
                : "Brak szczegółowych zadań do wyświetlenia"}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="w-full space-y-3">
      {error && <Alert message={error.message} type="error" />}
      <Collapse items={collapseItems} size="small" className="bg-transparent" />
    </div>
  );
};

/**
 * Masowa generacja zdjęć potraw (TRE-1830) — osobny, prosty widok. Nie ma tu kroków workflowu ani
 * zmian do zatwierdzenia jak przy `dish_agent`; jedyne, co trenerkę interesuje, to postęp i to,
 * ile run faktycznie kosztował.
 */
const DishImageGeneratorRequest = ({ request }: { request: AiAgentRequest }) => {
  const { t } = useTranslation(["dashboard"]);
  const navigate = useNavigate();
  const { closeDrawer } = useBackgroundTasks();
  const { deleteDishTaskMutation } = useDishTaskSave();
  const { cancelAgentRequestMutation } = useBackgroundTasksQuery({ enabled: false });

  const isFinished =
    request.status === "COMPLETED" ||
    request.status === "FAILED" ||
    request.status === "CANCELLED" ||
    request.status === "DELETED";

  const { runs, error } = useRealtimeRunsWithTag<any>(request.metadata?.tags || [], {
    enabled: Boolean(request.metadata?.accessToken && !isFinished),
    accessToken: request.metadata?.accessToken,
  });

  const run = runs?.find((item) => item.taskIdentifier === "generate-meals-plan-dish-images-task");
  const summary = request.data?.dishImages;
  // Zadania sprzed poprawki mają w `data` samo podsumowanie — task nadpisywał wtedy całą kolumnę
  // i gubił `planId`. Podsumowanie niesie ten sam identyfikator, więc stare wpisy też da się otworzyć.
  const planId = request.data?.planId ?? summary?.planId;
  const knownPlanType = request.data?.planType;

  const expected = getNumberValue(run?.metadata?.expected) ?? request.taskCount;
  const generated = summary?.generated ?? getNumberValue(run?.metadata?.generated) ?? 0;
  const failed = summary?.failed ?? getNumberValue(run?.metadata?.failed) ?? 0;
  const costUsd = summary?.costUsd ?? getNumberValue(run?.metadata?.costUsd);
  const finishedImages = generated + failed;
  const progressPercent = expected ? Math.min(100, Math.round((finishedImages / expected) * 100)) : 0;

  // Nazwa planu i (dla diety klientki) jej id — jedno i drugie potrzebne, żeby dało się z drawera
  // wejść w plan, którego dotyczy zadanie. Rozwiązujemy to tutaj, a nie przy tworzeniu zgłoszenia,
  // bo dzięki temu działa też dla zadań utworzonych zanim ten widok powstał.
  const planQuery = useQuery(["dishImagesPlanContext", planId, knownPlanType], {
    enabled: Boolean(planId),
    queryFn: async () => {
      const lookUpTemplate = async () => {
        const { data, error } = await supabase.from("meals_plan").select("name").eq("id", planId!).maybeSingle();
        if (error) throw new Error(error.message);
        return data ? { name: data.name, traineeId: null, planType: "template" as const } : null;
      };

      const lookUpClientPlan = async () => {
        const { data, error } = await supabase
          .from("client_meals_plan")
          .select("name, traineeId")
          .eq("id", planId!)
          .maybeSingle();
        if (error) throw new Error(error.message);
        return data ? { name: data.name, traineeId: data.traineeId, planType: "client" as const } : null;
      };

      // Znany typ sprawdzamy pierwszy, ale i tak próbujemy obu tabel: stare zadania nie mają
      // zapisanego `planType` i bez tego dieta klientki wyświetlałaby się jako szablon.
      const [first, second] =
        knownPlanType === "client" ? [lookUpClientPlan, lookUpTemplate] : [lookUpTemplate, lookUpClientPlan];

      return (await first()) ?? (await second()) ?? null;
    },
  });

  const plan = planQuery.data;
  const planType = plan?.planType ?? knownPlanType ?? "template";
  // W react-query 4 zapytanie z `enabled: false` zostaje na zawsze w stanie `loading`, więc
  // `isLoading` samo w sobie znaczyłoby „Wczytywanie..." bez końca dla wpisów bez `planId`.
  const isResolvingPlan = Boolean(planId) && planQuery.isInitialLoading;
  const canOpenPlan = Boolean(planId) && Boolean(plan) && (plan?.planType === "template" || Boolean(plan?.traineeId));

  const openPlan = () => {
    if (!planId || !plan) return;

    navigate(
      plan.planType === "client"
        ? `/trainee/details/${plan.traineeId}?planId=${planId}&tab=nutrition&nutritionTab=schedule`
        : `/nutrition/plans/${planId}`,
    );
    closeDrawer();
  };

  const canCancelTask =
    !isFinished &&
    (!run || ["PENDING_VERSION", "QUEUED", "DEQUEUED", "EXECUTING", "WAITING", "DELAYED"].includes(run.status));
  const canDeleteTask = request.status === "FAILED" || request.status === "COMPLETED" || request.status === "CANCELLED";

  const collapseItems = [
    {
      key: request.id,
      label: (
        <div className="flex w-full items-center justify-between gap-2">
          <span className="font-medium">
            [{getAgentTitle(request.agentType, t)}] {plan?.name ? `${plan.name} — ` : ""}
            {dayjs(request.createdAt).format("DD.MM.YYYY HH:mm")}
          </span>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {getStatusBadge(request.status, run?.status as RunStatus | undefined)}
            {/* Zapis do planu następuje dopiero po całym batchu, a anulowanie rodzica ubija też
                podzadania — więc nic nie trafi do planu, choć za to, co już poszło do modelu, jest
                zapłacone. Lepiej powiedzieć to wprost niż obiecywać częściowy wynik. */}
            {canCancelTask && (
              <Popconfirm
                title="Anulować generowanie?"
                description="Zatrzymamy zadanie. Zdjęcia, które zdążyły powstać, NIE trafią do planu — koszt już poniesiony przepadnie."
                onConfirm={() => cancelAgentRequestMutation.mutate({ requestId: request.id })}
                okText="Tak, anuluj"
                cancelText="Nie"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<CloseOutlined />} loading={cancelAgentRequestMutation.isLoading}>
                  Anuluj
                </Button>
              </Popconfirm>
            )}
            {canDeleteTask && (
              <Popconfirm
                title="Usunąć zadanie?"
                description="Zniknie z listy. Wygenerowane zdjęcia zostają w planie."
                onConfirm={() => deleteDishTaskMutation.mutate({ requestId: request.id })}
                okText="Tak, usuń"
                cancelText="Anuluj"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<DeleteOutlined />} loading={deleteDishTaskMutation.isLoading} />
              </Popconfirm>
            )}
          </div>
        </div>
      ),
      children: (
        <div className="space-y-3">
          {error && <Alert message={error.message} type="error" />}

          <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2">
            <div className="min-w-0">
              <Typography.Text className="text-xs text-gray-500">
                {planType === "client" ? "Dieta klientki" : "Szablon diety"}
              </Typography.Text>
              <div className="truncate text-sm font-medium">
                {isResolvingPlan ? "Wczytywanie..." : (plan?.name ?? "Plan niedostępny")}
              </div>
            </div>
            <Button
              size="small"
              type="primary"
              ghost
              icon={<FileTextOutlined />}
              disabled={!canOpenPlan}
              onClick={openPlan}
            >
              Otwórz plan
            </Button>
          </div>

          {!isFinished && expected ? (
            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <Typography.Text className="text-sm text-gray-600">Generowanie zdjęć</Typography.Text>
                <Typography.Text className="shrink-0 text-xs text-gray-500">
                  {finishedImages}/{expected}
                </Typography.Text>
              </div>
              <Progress
                percent={progressPercent}
                size="small"
                status="active"
                strokeColor={{ "0%": "#1890ff", "100%": "#52c41a" }}
              />
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
              <Typography.Text className="text-xs text-gray-500">Wygenerowane</Typography.Text>
              <div className="text-sm font-medium">
                {generated}
                {expected ? ` / ${expected}` : ""}
              </div>
            </div>
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
              <Typography.Text className="text-xs text-gray-500">Koszt</Typography.Text>
              <div className="text-sm font-medium">{typeof costUsd === "number" ? `$${costUsd.toFixed(2)}` : "—"}</div>
            </div>
            {failed ? (
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
                <Typography.Text className="text-xs text-red-600">Nieudane</Typography.Text>
                <div className="text-sm font-medium text-red-700">{failed}</div>
              </div>
            ) : null}
            {summary?.updatedLibraryDishes ? (
              <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
                <Typography.Text className="text-xs text-gray-500">Zapisane w bibliotece</Typography.Text>
                <div className="text-sm font-medium">{summary.updatedLibraryDishes}</div>
              </div>
            ) : null}
            {summary?.timeMs ? (
              <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
                <Typography.Text className="text-xs text-gray-500">Czas generowania</Typography.Text>
                <div className="text-sm font-medium">{formatDurationMs(summary.timeMs)}</div>
              </div>
            ) : null}
          </div>

          {summary?.skippedByLimit ? (
            <Alert
              type="warning"
              showIcon
              message={`Pominięto ${summary.skippedByLimit} potraw — limit jednego uruchomienia. Kliknij ponownie, żeby dokończyć.`}
            />
          ) : null}

          {summary?.model ? (
            <Typography.Text className="text-xs text-gray-400">Model: {summary.model}</Typography.Text>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="w-full">
      <Collapse items={collapseItems} size="small" className="bg-transparent" />
    </div>
  );
};

type StudioClipsData = {
  sourceMediaId?: string;
  sourceTitle?: string;
  /** "segments" to kopanie klocków do reklam (etap G); brak = klasyczna produkcja klipów. */
  mode?: string;
  candidates?: number;
  segments?: number;
  readyClips?: number;
  qcFailedClips?: number;
  failedClips?: number;
  time?: number;
  error?: string;
};

const STUDIO_RUN_ACTIVE_STATUSES = ["PENDING_VERSION", "QUEUED", "DEQUEUED", "EXECUTING", "WAITING", "DELAYED"];

/** Skrót wyniku na jedną linię — pełne liczby są w rozwinięciu, nagłówek ma się nie rozjeżdżać. */
const getStudioSummary = (data: StudioClipsData) => {
  if (data.mode === "segments") {
    return typeof data.segments === "number" ? `${data.segments} klocków do reklam` : null;
  }

  if (typeof data.readyClips !== "number") {
    return typeof data.candidates === "number" ? `${data.candidates} kandydatów` : null;
  }

  const parts = [`${data.readyClips} gotowych`];
  if (data.qcFailedClips) parts.push(`${data.qcFailedClips} odrzuconych przez kontrolę jakości`);
  if (data.failedClips) parts.push(`${data.failedClips} z błędem`);
  return parts.join(", ");
};

const StudioStat = ({ label, value, danger = false }: { label: string; value: string; danger?: boolean }) => (
  <div className={cn("rounded-md border px-3 py-2", danger ? "border-red-200 bg-red-50" : "border-gray-200 bg-white")}>
    <Typography.Text className={cn("text-xs", danger ? "text-red-600" : "text-gray-500")}>{label}</Typography.Text>
    <div className={cn("text-sm font-medium", danger && "text-red-700")}>{value}</div>
  </div>
);

/**
 * Produkcja klipów (TRE-1975): o klipach decyduje się w panelu /studio, więc wpis w drawerze jest
 * pokwitowaniem przebiegu — status, skrót wyniku i wejście do panelu. Wygląda jak pozostałe
 * zadania (rozwijana karta), bo wcześniej była to goła linia tekstu rozpychająca listę.
 */
const StudioClipsRequest = ({ request }: { request: AiAgentRequest }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { closeDrawer } = useBackgroundTasks();
  const { deleteDishTaskMutation } = useDishTaskSave();
  const { cancelAgentRequestMutation } = useBackgroundTasksQuery({ enabled: false });
  const [isMarkingCompleted, setIsMarkingCompleted] = useState(false);

  const data = (request.data ?? {}) as StudioClipsData;
  const isSegments = data.mode === "segments";
  const summary = getStudioSummary(data);
  const productionTime = formatDurationMs(data.time);

  const isFinished = ["TO_EVALUATE", "COMPLETED", "FAILED", "CANCELLED", "DELETED"].includes(request.status);

  const { runs, error } = useRealtimeRunsWithTag<any>(request.metadata?.tags || [], {
    enabled: Boolean(request.metadata?.accessToken && !isFinished),
    accessToken: request.metadata?.accessToken,
  });
  const run = runs?.find((item) => item.taskIdentifier === "produce-video-clips-task");
  // Etapu nie da się pokazać paskiem: rendery klipów lecą batchem i nie raportują postępu rodzicowi.
  const stage = typeof run?.metadata?.operationType === "string" ? run.metadata.operationType : null;
  // Nagłówek ma jedną linię na skrót: wynik, a gdy go nie ma — etap przebiegu albo powód błędu.
  const subtitle = isFinished ? (summary ?? data.error ?? null) : (stage ?? summary);

  // `taskId` startuje jako id zgłoszenia i dopiero trigger podmienia je na id runa — dopóki tego nie
  // zrobi, anulowanie nie ma czego zatrzymać.
  const hasRun = Boolean(request.taskId) && request.taskId !== request.id;
  const canCancelTask = !isFinished && hasRun && (!run || STUDIO_RUN_ACTIVE_STATUSES.includes(run.status));

  const markAsCompleted = async () => {
    // `.single()` zamienia „0 wierszy" (RLS odfiltrowało zgłoszenie) w błąd — bez tego
    // PostgREST odpowiada 204 i wpis wisiałby w liczniku bez żadnego komunikatu.
    await supabase
      .from("ai_agent_request")
      .update({ status: "COMPLETED", updatedAt: new Date().toISOString() })
      .eq("id", request.id)
      .select("id")
      .single()
      .throwOnError();

    await queryClient.invalidateQueries({ queryKey: ["ai_agent_request"] });
  };

  const handleMarkAsCompleted = async () => {
    setIsMarkingCompleted(true);
    try {
      await markAsCompleted();
    } catch (error) {
      void message.error(`Nie udało się oznaczyć zadania: ${getErrorMessage(error)}`);
    } finally {
      setIsMarkingCompleted(false);
    }
  };

  const openStudio = async () => {
    // Wejście do panelu jest właśnie tą oceną, na którą zgłoszenie czeka — inaczej wisiałoby
    // w liczniku „do oceny" mimo że wyniki zostały obejrzane.
    if (request.status === "TO_EVALUATE") {
      await markAsCompleted();
    }

    navigate(isSegments ? "/studio?tab=ads" : "/studio");
    closeDrawer();
  };

  const collapseItems = [
    {
      key: request.id,
      label: (
        <div className="flex w-full items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="truncate font-medium">
              [{isSegments ? "Klocki do reklam" : "Produkcja klipów"}] {data.sourceTitle ?? "nagranie"} —{" "}
              {dayjs(request.createdAt).format("DD.MM.YYYY HH:mm")}
            </div>
            {subtitle && (
              <div className={cn("truncate text-xs", request.status === "FAILED" ? "text-red-500" : "text-gray-500")}>
                {subtitle}
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <span className="whitespace-nowrap">
              {getStatusBadge(request.status, run?.status as RunStatus | undefined)}
            </span>
            {canCancelTask && (
              <Popconfirm
                title="Anulować produkcję?"
                description="Zatrzymamy zadanie. Klipy, które zdążyły się wyrenderować, zostaną w panelu Studio."
                onConfirm={() => cancelAgentRequestMutation.mutate({ requestId: request.id })}
                okText="Tak, anuluj"
                cancelText="Nie"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<CloseOutlined />} loading={cancelAgentRequestMutation.isLoading}>
                  Anuluj
                </Button>
              </Popconfirm>
            )}
            {request.status === "TO_EVALUATE" && (
              <Tooltip title="Oznacz jako obejrzane">
                <Popconfirm
                  title="Oznaczyć jako zakończone?"
                  description="Zniknie z licznika. Klipy zostają w panelu Studio."
                  onConfirm={() => void handleMarkAsCompleted()}
                  okText="Tak, oznacz"
                  cancelText="Anuluj"
                >
                  <Button type="primary" ghost icon={<CheckOutlined />} loading={isMarkingCompleted} />
                </Popconfirm>
              </Tooltip>
            )}
            {isFinished && (
              <Popconfirm
                title="Usunąć zadanie?"
                description="Zniknie z listy. Wyprodukowane klipy zostają w panelu Studio."
                onConfirm={() => deleteDishTaskMutation.mutate({ requestId: request.id })}
                okText="Tak, usuń"
                cancelText="Anuluj"
                okButtonProps={{ danger: true }}
              >
                <Button danger icon={<DeleteOutlined />} loading={deleteDishTaskMutation.isLoading} />
              </Popconfirm>
            )}
          </div>
        </div>
      ),
      children: (
        <div className="space-y-3">
          {error && <Alert message={error.message} type="error" />}
          {data.error && <Alert type="error" showIcon message={data.error} />}

          <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2">
            <div className="min-w-0">
              <Typography.Text className="text-xs text-gray-500">Nagranie źródłowe</Typography.Text>
              <div className="truncate text-sm font-medium">{data.sourceTitle ?? "—"}</div>
            </div>
            <Button
              size="small"
              type="primary"
              ghost
              icon={<VideoCameraOutlined />}
              onClick={() => void openStudio()}
              className="shrink-0"
            >
              Otwórz Studio
            </Button>
          </div>

          {!isFinished && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
              <Typography.Text className="text-xs text-blue-700">Etap</Typography.Text>
              <div className="text-sm font-medium text-blue-900">{stage ?? "Przygotowanie kandydatów"}</div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            {isSegments ? (
              <StudioStat label="Klocki do reklam" value={`${data.segments ?? 0}`} />
            ) : (
              <>
                <StudioStat
                  label="Gotowe klipy"
                  value={`${data.readyClips ?? 0}${data.candidates ? ` / ${data.candidates}` : ""}`}
                />
                {data.qcFailedClips ? <StudioStat label="Odrzucone przez QC" value={`${data.qcFailedClips}`} /> : null}
                {data.failedClips ? <StudioStat label="Z błędem" value={`${data.failedClips}`} danger /> : null}
              </>
            )}
            {productionTime ? <StudioStat label="Czas produkcji" value={productionTime} /> : null}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full">
      {/* `ant-collapse-header-text` ma `min-width: auto`, więc bez tego rozpycha nagłówek
          do szerokości tytułu i wypycha przyciski poza kartę zamiast uciąć tekst. */}
      <Collapse items={collapseItems} size="small" className="bg-transparent [&_.ant-collapse-header-text]:min-w-0" />
    </div>
  );
};

const AiRequestListItemPure = ({ request }: { request: AiAgentRequest }) => {
  if (request.agentType === "nutrition_generator") {
    return <NutritionGeneratorRequest request={request} />;
  }

  if (request.agentType === "studio_clips") {
    return <StudioClipsRequest request={request} />;
  }

  if (request.agentType === "dish_image_generator") {
    return <DishImageGeneratorRequest request={request} />;
  }

  return <DishAgentRequest request={request} />;
};

export const AiRequestListItem = memo(AiRequestListItemPure);
